import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthContext } from './auth';
import {
    getSchoolDashboardService,
    getPartnerDashboardService,
    getClassDashboardService,
    getIndividualDashboardService,
    getNationalDashboardService,
    getSchoolNetworkDashboardService,
} from '../services/dynamicDashboard.service';
import { FEATURE_KEY } from '../utils/constants';

const DashboardContext = createContext(null);

/** How long a loaded dashboard is reused from memory before it is fetched again. */
const IN_MEMORY_FRESH_MS = 60 * 1000;

/*
 * Static dashboard data for a page that shows a dashboard outside its own route (the
 * program-creation preview's "View Impact" tab). Inside this provider useDashboard returns
 * `data` as the dashboard payload, never loading, and fetches nothing — the section pages
 * render exactly as they do with live data.
 */
const DashboardMockContext = createContext(null);
export const DashboardMockProvider = ({ data, children }) => (
    <DashboardMockContext.Provider value={data}>{children}</DashboardMockContext.Provider>
);

const DASHBOARD_SERVICE_MAP = {
    [FEATURE_KEY.SCHOOL_DASHBOARD]: (params) => getSchoolDashboardService(params),
    [FEATURE_KEY.PARTNER_DASHBOARD]: (params) => getPartnerDashboardService(params),
    [FEATURE_KEY.CLASS_DASHBOARD]: (params) => getClassDashboardService(params),
    [FEATURE_KEY.INDIVIDUAL_DASHBOARD]: (params) => getIndividualDashboardService(params),
    [FEATURE_KEY.NATIONAL_DASHBOARD]: (params) => getNationalDashboardService(params),
    [FEATURE_KEY.SCHOOL_NETWORK_DASHBOARD]: (params) => getSchoolNetworkDashboardService(params),
};

/*
 * National is one dashboard per partner × country (datahouse / API, 6 Oct 2026). The chosen
 * country is NOT in the URL (user decision): it lives in this context and, so a reload of the
 * same tab keeps it, in sessionStorage under the partner's id. A new tab or a shared link starts
 * without one — the API then answers with the profile country / the partner's only National, or
 * with nothing, and the page asks for a choice in the workspace menu.
 */
const NATIONAL_COUNTRY_KEY = 'dq_seal_cl_national_country';
const normaliseIso2 = (value) => {
    const code = String(value || '').trim().toUpperCase();
    return /^[A-Z]{2}$/.test(code) ? code : null;
};
const readStoredCountry = (partnerId) => {
    if (!partnerId) return null;
    try { return normaliseIso2(window.sessionStorage.getItem(`${NATIONAL_COUNTRY_KEY}:${partnerId}`)); } catch (e) { return null; }
};
const writeStoredCountry = (partnerId, code) => {
    if (!partnerId) return;
    try {
        if (code) window.sessionStorage.setItem(`${NATIONAL_COUNTRY_KEY}:${partnerId}`, code);
        else window.sessionStorage.removeItem(`${NATIONAL_COUNTRY_KEY}:${partnerId}`);
    } catch (e) { /* storage unavailable — the choice just does not survive a reload */ }
};

export const getDashboardTypeFromPath = (path) => {
    if (path.startsWith('/dq_pro/school-dashboard')) return FEATURE_KEY.SCHOOL_DASHBOARD;
    if (path.startsWith('/dq_pro/partner-dashboard')) return FEATURE_KEY.PARTNER_DASHBOARD;
    if (path.startsWith('/dq_pro/class-dashboard')) return FEATURE_KEY.CLASS_DASHBOARD;
    if (path.startsWith('/dq_pro/individual-dashboard')) return FEATURE_KEY.INDIVIDUAL_DASHBOARD;
    if (path.startsWith('/dq_pro/national-dashboard')) return FEATURE_KEY.NATIONAL_DASHBOARD;
    if (path.startsWith('/dq_pro/network-dashboard')) return FEATURE_KEY.SCHOOL_NETWORK_DASHBOARD;
    return null;
};

export const DashboardProvider = ({ children }) => {
    const [dashboardDataMap, setDashboardDataMap] = useState({});
    const [loadingMap, setLoadingMap] = useState({});
    // National only: the partner's National dashboards [{country, uuid}] from the last response —
    // null until the first answer, [] when the partner has none.
    const [nationalCountries, setNationalCountries] = useState(null);
    // National only: the country chosen in the workspace menu (see the note above readStoredCountry)
    const { authUser } = useContext(AuthContext) || {};
    const partnerId = authUser?.dq_insider_id;
    const [nationalCountry, setNationalCountry] = useState(() => readStoredCountry(partnerId));
    useEffect(() => { setNationalCountry(readStoredCountry(partnerId)); }, [partnerId]);
    const selectNationalCountry = useCallback((code) => {
        const c = normaliseIso2(code);
        setNationalCountry(c);
        writeStoredCountry(partnerId, c);
    }, [partnerId]);
    const location = useLocation();

    const isDashboardPath = location.pathname.startsWith('/dq_pro/school-dashboard') ||
        location.pathname.startsWith('/dq_pro/partner-dashboard') ||
        location.pathname.startsWith('/dq_pro/class-dashboard') ||
        location.pathname.startsWith('/dq_pro/individual-dashboard') ||
        location.pathname.startsWith('/dq_pro/national-dashboard') ||
        location.pathname.startsWith('/dq_pro/network-dashboard');

    const stateRef = useRef({ dashboardDataMap, loadingMap });
    useEffect(() => {
        stateRef.current = { dashboardDataMap, loadingMap };
    }, [dashboardDataMap, loadingMap]);
    // When each dashboard section was last loaded. The in-memory copy is reused only for a
    // minute — enough for one page's cards to share a load — so coming back to a
    // dashboard later shows current numbers, not the first visit's (School 41 next
    // to Class 50 until the page was refreshed).
    const loadedAtRef = useRef({});

    const fetchDashboardData = useCallback((section_code, card_sections, country) => {
        if (!isDashboardPath) return;

        const dashboardType = getDashboardTypeFromPath(location.pathname);
        if (!dashboardType) return;

        const serviceFn = DASHBOARD_SERVICE_MAP[dashboardType];
        if (!serviceFn) return;

        const isNational = dashboardType === FEATURE_KEY.NATIONAL_DASHBOARD;
        // National: one entry per country; 'auto' = no country chosen yet (the API picks the
        // profile country or the partner's only National)
        const cacheKey = isNational
            ? `${dashboardType}_${country || 'auto'}_${section_code || 'all'}`
            : `${dashboardType}_${section_code || 'all'}`;
        const { dashboardDataMap: currentData, loadingMap: currentLoading } = stateRef.current;
        if (currentLoading[cacheKey]) return;

        // If card_sections is specified and all requested sections are already in memory
        // (and were loaded within the last minute), skip network fetch
        // Each section keeps its own clock: one page's sections loading must not make
        // another page's older sections look fresh.
        if (card_sections && currentData[cacheKey]?.cards?.sections) {
            const requestedList = (Array.isArray(card_sections) ? card_sections : String(card_sections).split(',')).map(s => s.trim()).filter(Boolean);
            const loadedSectionCodes = new Set((currentData[cacheKey].cards.sections || []).map(s => s.section_code));
            const allFresh = requestedList.every(code => loadedSectionCodes.has(code)
                && Date.now() - (loadedAtRef.current[`${cacheKey}|${code}`] || 0) < IN_MEMORY_FRESH_MS);
            if (allFresh) return;
        }

        setLoadingMap(prev => ({ ...prev, [cacheKey]: true }));
        const normalizedCardSections = Array.isArray(card_sections) ? card_sections.join(',') : card_sections;
        const params = {
            ...(section_code ? { section_code } : {}),
            ...(isNational && country ? { country } : {}),
            ...(normalizedCardSections ? { card_sections: normalizedCardSections } : {}),
        };

        serviceFn(params)
            .then(res => {
                if (isNational && res && Array.isArray(res.available_countries)) {
                    setNationalCountries(res.available_countries);
                }
                if (res && res.hasOwnProperty('data')) {
                    if (res.data === null) {
                        setDashboardDataMap(prev => ({ ...prev, [cacheKey]: null }));
                    } else if (res.data) {
                        // An 'auto' National answer is also filed under the country the API chose
                        // (registry.country), so adopting that choice does not fetch it a second time.
                        const resolved = isNational && !country && res.data.registry?.country
                            ? `${dashboardType}_${String(res.data.registry.country).toUpperCase()}_${section_code || 'all'}`
                            : null;
                        const merge = (existing) => {
                            if (!existing) return res.data;
                            const existingSections = existing.cards?.sections || [];
                            const incomingSections = res.data.cards?.sections || [];
                            const mergedSectionsMap = new Map();
                            existingSections.forEach(sec => mergedSectionsMap.set(sec.section_code, sec));
                            incomingSections.forEach(sec => mergedSectionsMap.set(sec.section_code, sec));
                            return {
                                ...existing,
                                ...res.data,
                                cards: {
                                    ...(existing.cards || {}),
                                    ...(res.data.cards || {}),
                                    sections: Array.from(mergedSectionsMap.values())
                                }
                            };
                        };
                        const now = Date.now();
                        for (const sec of res.data.cards?.sections || []) {
                            loadedAtRef.current[`${cacheKey}|${sec.section_code}`] = now;
                            if (resolved) loadedAtRef.current[`${resolved}|${sec.section_code}`] = now;
                        }
                        setDashboardDataMap(prev => {
                            const next = { ...prev, [cacheKey]: merge(prev[cacheKey]) };
                            if (resolved) next[resolved] = merge(prev[resolved]);
                            return next;
                        });
                    } else {
                        setDashboardDataMap(prev => ({ ...prev, [cacheKey]: null }));
                    }
                } else {
                    setDashboardDataMap(prev => ({ ...prev, [cacheKey]: null }));
                }
            })
            .catch(err => {
                console.error('Failed to load dynamic dashboard:', err);
                setDashboardDataMap(prev => ({ ...prev, [cacheKey]: null }));
            })
            .finally(() => {
                setLoadingMap(prev => ({ ...prev, [cacheKey]: false }));
            });
    }, [isDashboardPath, location.pathname]);

    return (
        <DashboardContext.Provider value={{ dashboardDataMap, loadingMap, fetchDashboardData, nationalCountries, nationalCountry, selectNationalCountry }}>
            {children}
        </DashboardContext.Provider>
    );
};

export const useDashboard = (options = {}) => {
    const mockData = useContext(DashboardMockContext);
    const context = useContext(DashboardContext);
    if (!context && !mockData) {
        throw new Error('useDashboard must be used within a DashboardProvider');
    }

    const rawCardSections = typeof options === 'string' ? options : options?.card_sections;
    const card_sections = Array.isArray(rawCardSections) ? rawCardSections.join(',') : rawCardSections;
    const passive = Boolean(mockData) || Boolean(typeof options === 'object' && options?.passive);

    const { dashboardDataMap = {}, loadingMap = {}, fetchDashboardData, nationalCountries, nationalCountry, selectNationalCountry } = context || {};
    const location = useLocation();
    const isClassDashboard = location.pathname.startsWith('/dq_pro/class-dashboard');
    const urlParams = new URLSearchParams(location.search);
    const section_code = isClassDashboard ? urlParams.get('section_code') : null;

    const dashboardType = getDashboardTypeFromPath(location.pathname);
    const isNational = dashboardType === FEATURE_KEY.NATIONAL_DASHBOARD;
    const country = isNational ? nationalCountry : null;
    const cacheKey = dashboardType
        ? (isNational ? `${dashboardType}_${country || 'auto'}_${section_code || 'all'}` : `${dashboardType}_${section_code || 'all'}`)
        : (section_code || 'all');

    useEffect(() => {
        if (!passive) {
            fetchDashboardData(section_code, card_sections, country);
        }
    }, [section_code, card_sections, country, passive, fetchDashboardData]);

    const dashboardData = dashboardDataMap[cacheKey];

    // National without a chosen country: the API chose one (profile country / the only one) — adopt
    // it as the selection so every page and the menu agree from here on.
    const resolvedCountry = isNational && !country ? dashboardData?.registry?.country : null;
    useEffect(() => {
        if (resolvedCountry) selectNationalCountry(resolvedCountry);
    }, [resolvedCountry, selectNationalCountry]);

    if (mockData) {
        return { dashboardData: mockData, loading: false, country: null, availableCountries: null };
    }

    const isLoading = loadingMap[cacheKey] !== undefined
        ? Boolean(loadingMap[cacheKey])
        : (!passive && dashboardData === undefined);

    return {
        dashboardData,
        loading: isLoading,
        // National only
        country,
        availableCountries: isNational ? nationalCountries : null,
    };
};

/** The National country selection alone — for the workspace menu; fetches nothing. */
export const useNationalSelection = () => {
    const context = useContext(DashboardContext);
    if (!context) {
        throw new Error('useNationalSelection must be used within a DashboardProvider');
    }
    const { nationalCountry, selectNationalCountry } = context;
    return { nationalCountry, selectNationalCountry };
};
